/**
 * アプリ内の確認ダイアログ
 * ブラウザのconfirm()は、iOS Safariの「追加のダイアログを禁止」やアプリ内ブラウザで即falseになり、
 * 操作が何も言わずに効かなくなるため、確認はすべてこの部品で表示します。
 * 表示先の`ConfirmDialogHost`は`ScreenLayout`だけが描画し、確認の寿命は画面の寿命と一致します。
 */

import React, { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { Modal, Pressable, Text, View } from './WebPrimitives';
import { logError, logWarn } from '../utils/logger';

/** 確認の種類。'danger' は取り消せない操作・失う操作で、実行ボタンを危険色にし、初期focusを取消に置く */
export type ConfirmTone = 'default' | 'danger';

/** 確認ダイアログの依頼 */
export type ConfirmRequest = {
  /** 見出し。dialogのアクセシブル名にもなる */
  title: string;
  /** 本文。\n はそのまま改行して表示する */
  message: string;
  /** 実行ボタンの文言。動詞で書く（例: 'QRを発行'） */
  confirmLabel: string;
  /** 取消ボタンの文言。既定は 'キャンセル' */
  cancelLabel?: string;
  tone?: ConfirmTone;
};

/** 表示中の確認 */
type PendingConfirm = {
  id: number;
  /** 依頼時に最後にmountされていたhost。このhostだけが描画し、unmountで取消になる */
  ownerHostId: number;
  request: ConfirmRequest;
  resolve: (confirmed: boolean) => void;
};

/** 取消ボタンの既定文言 */
const DEFAULT_CANCEL_LABEL = 'キャンセル';

/** 同時に表示する確認は最大1件 */
let pendingConfirm: PendingConfirm | null = null;
/** mount中のhostのID。後ろほど新しい */
let mountedHostIds: number[] = [];
/** 次に払い出す確認ID */
let nextConfirmId = 1;
/** 次に払い出すhostID */
let nextHostId = 1;
/** hostの購読先 */
const listeners = new Set<() => void>();

/**
 * 表示中の確認を差し替え、hostへ知らせる
 * @param {PendingConfirm | null} nextPendingConfirm - 次の表示内容
 * @returns {void}
 */
const setPendingConfirm = (nextPendingConfirm: PendingConfirm | null) => {
  pendingConfirm = nextPendingConfirm;
  listeners.forEach((listener) => listener());
};

/**
 * 表示中の確認を解決して閉じる。IDが一致しない（解決済み）場合は何もしない
 * @param {number} confirmId - 確認ID
 * @param {boolean} confirmed - 実行ボタンが押された場合true
 * @returns {void}
 */
const settleConfirm = (confirmId: number, confirmed: boolean) => {
  if (pendingConfirm?.id !== confirmId) {
    return;
  }
  const { resolve } = pendingConfirm;
  setPendingConfirm(null);
  resolve(confirmed);
};

/**
 * hostから表示中の確認を購読する
 * @param {() => void} listener - 変更通知
 * @returns {() => void} 購読解除
 */
const subscribePendingConfirm = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * 表示中の確認を返す
 * @returns {PendingConfirm | null} 表示中の確認
 */
const getPendingConfirmSnapshot = () => pendingConfirm;

/**
 * 確認を表示し、実行ボタンが押された場合だけtrueで解決する
 * 取消・Escape・背景タップ・画面から離れる（hostのunmount）はfalse。
 * 確認の表示中に呼ばれた場合は、表示中の確認を残したまま即falseを返す。
 * @param {ConfirmRequest} request - 確認の内容
 * @returns {Promise<boolean>} 実行してよい場合true。hostが無い場合はreject
 */
export const confirmAction = (request: ConfirmRequest): Promise<boolean> => {
  const ownerHostId = mountedHostIds[mountedHostIds.length - 1];
  if (ownerHostId === undefined) {
    // 黙ってfalseを返すと、ボタンが何も言わずに効かない今回の不具合と同じ形になる
    logError('確認ダイアログの表示先がないため、確認を表示できませんでした。', {
      title: request.title,
    });
    return Promise.reject(new Error('ConfirmDialogHostがmountされていません。'));
  }

  if (pendingConfirm) {
    logWarn('確認ダイアログの表示中に別の確認が要求されたため、後の要求を取り消しました。', {
      title: request.title,
      openTitle: pendingConfirm.request.title,
    });
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const id = nextConfirmId;
    nextConfirmId += 1;
    setPendingConfirm({ id, ownerHostId, request, resolve });
  });
};

/**
 * 確認ダイアログの表示内容
 * @param {{ pending: PendingConfirm }} props - 表示中の確認
 * @returns {React.JSX.Element} 確認ダイアログ
 */
const ConfirmDialogView = ({ pending }: { pending: PendingConfirm }): React.JSX.Element => {
  const { id, request } = pending;
  const isDanger = request.tone === 'danger';
  const titleId = useId();
  const messageId = useId();
  /** 取消ボタン */
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  /** 実行ボタン */
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Modal
      accessibilityDescribedBy={messageId}
      accessibilityLabelledBy={titleId}
      initialFocusRef={isDanger ? cancelButtonRef : confirmButtonRef}
      onRequestClose={() => settleConfirm(id, false)}
      testID="confirm-dialog"
      transparent
      visible
    >
      <View className="min-h-0 flex-1 justify-center overflow-hidden bg-black/60 px-4 py-8">
        <View className="mx-auto max-h-full w-full max-w-[420px] overflow-y-auto border-4 border-app-quest-night bg-app-card p-4 shadow-[6px_6px_0_rgba(22,36,58,0.38)]">
          <Text
            aria-level={2}
            className="text-xl font-extrabold text-app-quest-night"
            id={titleId}
            role="heading"
          >
            {request.title}
          </Text>
          <Text
            className="mt-3 whitespace-pre-line text-base leading-7 text-app-text"
            id={messageId}
          >
            {request.message}
          </Text>
          <View className="mt-5 gap-3">
            <Pressable
              accessibilityRole="button"
              className={`min-h-[48px] w-full items-center justify-center border-2 px-4 py-3 ${isDanger ? 'border-[#7B1A1A] bg-app-danger' : 'border-[#0F1A2E] bg-app-quest-night'}`}
              onPress={() => settleConfirm(id, true)}
              ref={confirmButtonRef}
              testID="confirm-dialog-confirm-button"
            >
              <Text className="text-center text-base font-extrabold text-white">
                {request.confirmLabel}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              className="min-h-[48px] w-full items-center justify-center border-2 border-app-quest-night bg-app-panel px-4 py-3"
              onPress={() => settleConfirm(id, false)}
              ref={cancelButtonRef}
              testID="confirm-dialog-cancel-button"
            >
              <Text className="text-center text-base font-extrabold text-app-quest-night">
                {request.cancelLabel ?? DEFAULT_CANCEL_LABEL}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/**
 * 確認の表示先。ScreenLayoutだけが描画する
 * 依頼時に最後にmountされていたhostが描画し、そのhostがunmountされると確認はfalseで閉じる。
 * @returns {React.JSX.Element | null} 表示中の確認ダイアログ
 */
export const ConfirmDialogHost = (): React.JSX.Element | null => {
  const [hostId] = useState(() => {
    const id = nextHostId;
    nextHostId += 1;
    return id;
  });
  const pending = useSyncExternalStore(
    subscribePendingConfirm,
    getPendingConfirmSnapshot,
    getPendingConfirmSnapshot,
  );

  useEffect(() => {
    mountedHostIds = [...mountedHostIds, hostId];
    return () => {
      mountedHostIds = mountedHostIds.filter((mountedHostId) => mountedHostId !== hostId);
      if (pendingConfirm?.ownerHostId === hostId) {
        // 離れた画面の確認が後から実行されないよう、取消として閉じる
        settleConfirm(pendingConfirm.id, false);
      }
    };
  }, [hostId]);

  if (!pending || pending.ownerHostId !== hostId) {
    return null;
  }

  return <ConfirmDialogView key={pending.id} pending={pending} />;
};
